/**
 * Appearance dialog - the org-admin entry point to the tenant's design tokens
 * (brand / surface / text colours, display + body fonts).
 *
 * It hosts the very same `ThemeEditor` the staff tenant view uses, writing the
 * very same record (`Tenant.settings["theme"]`) through the org-scoped endpoint
 * instead of the staff one - this surface exists to extend that editor's
 * visibility to admins, not to fork it. Because the theme lives on the Tenant,
 * a save applies tenant-wide (every organisation under it, the consumer app,
 * and branded documents), which the intro copy states outright.
 *
 * Saving invalidates `org-context`, so `TenantThemeProvider` re-applies the
 * tokens onto `:root` and the admin sees the new theme without a reload.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import api from '@/admin/lib/api'
import { ThemeEditor } from '@/admin/components/theme-editor'
import { useOrg } from '@/admin/hooks/use-org'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { toastApiError } from '@/shared/lib/api-error'
import type { ThemeOverrides } from '@/shared/lib/theme'

interface ThemeResponse {
  theme?: ThemeOverrides
  primary_colour?: string | null
}

export function AppearanceDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const { orgUuid } = useOrg()
  const queryClient = useQueryClient()

  const { data, isPending, isError } = useQuery<ThemeResponse>({
    queryKey: ['org-theme', orgUuid],
    queryFn: () =>
      api.get(`/organisations/${orgUuid}/theme/`).then((res) => res.data),
    // Enabled on mount, NOT gated on `open`: this dialog is always mounted, so
    // `&& open` left the observer disabled and the DETAIL policy's
    // refetchOnMount:'always' never fired. After a reload the query then served
    // the stale persisted cache without ever refetching, so the modal showed the
    // pre-save theme (the family reverted). The theme payload is tiny, so the
    // eager GET on the settings tab is cheap.
    enabled: Boolean(orgUuid),
  })

  const save = useMutation({
    mutationFn: (theme: ThemeOverrides) =>
      api
        .put(`/organisations/${orgUuid}/theme/`, { theme })
        .then((res) => res.data as ThemeResponse),
    onSuccess: (resp) => {
      toast.success(t('appearance.save_success', 'Appearance saved'))
      // Write the just-saved value into the cache synchronously. The PUT
      // response IS the new state, so we must not wait for an async refetch to
      // learn it: `invalidateQueries` alone races a page reload - the throttled
      // IndexedDB persist can still hold the pre-save value, and on reload the
      // (still-"fresh" under staleTime) stale entry is served to the re-enabled
      // modal query without a network fetch, so the theme appears to revert.
      queryClient.setQueryData(['org-theme', orgUuid], resp)
      // setQueryData already holds the authoritative server value, so only mark
      // the query stale for future mounts - refetchType:'none' skips the
      // redundant active refetch the open dialog would otherwise trigger.
      queryClient.invalidateQueries({
        queryKey: ['org-theme', orgUuid],
        refetchType: 'none',
      })
      // Re-applies the tokens live (TenantThemeProvider reads org-context).
      queryClient.invalidateQueries({ queryKey: ['org-context'] })
    },
    onError: (err) =>
      toastApiError(err, t('appearance.save_error', 'Failed to save appearance')),
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* `bare` + an explicit height: the editor manages its own scrolling (the
          token list scrolls in its column), so the dialog must NOT wrap the
          whole thing in one scroll region - that is what made the popup as tall
          as the token list and left dead space beside the preview. */}
      <DialogContent
        bare
        data-testid="appearance-dialog"
        className="h-[min(88vh,760px)] sm:max-w-5xl"
      >
        <DialogHeader className="shrink-0 border-b-[0.5px] border-b-[var(--rule)] px-6 pt-6 pb-4">
          <DialogTitle>{t('appearance.title', 'Appearance')}</DialogTitle>
          <DialogDescription>
            {t(
              'appearance.description',
              'Colours and fonts for your admin, consumer, and document surfaces.',
            )}
          </DialogDescription>
        </DialogHeader>

        {isPending ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-[var(--ink-3)]" />
          </div>
        ) : isError ? (
          <div className="flex flex-1 items-center justify-center px-6 text-center">
            <p className="text-[13px] text-[var(--ink-3)]">
              {t(
                'appearance.load_error',
                'Appearance settings could not be loaded. Close this dialog and try again.',
              )}
            </p>
          </div>
        ) : (
          <div className="min-h-0 flex-1 p-6">
            <ThemeEditor
              theme={data?.theme ?? {}}
              seedKey={orgUuid ?? ''}
              saving={save.isPending}
              variant="dialog"
              scopeNote={t(
                'appearance.scope_note',
                'Every token below shows the value in use - the platform default until you change it. Changes apply tenant-wide, everywhere your brand appears, and any token can be reset to fall back to the platform default.',
              )}
              onSave={(theme) => save.mutate(theme)}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
